import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Detects when a new frontend build has been deployed and returns state
 * for a UI banner to prompt a reload. See ARCHITECTURE section below.
 *
 * How it works:
 *  1. On mount, fetch /asset-manifest.json?t=<ts> and remember the current
 *     entrypoint hash (e.g. "static/js/main.3456a1b4.js").
 *  2. Every POLL_MS, plus on tab-visible + on window focus, fetch again with
 *     a cache-buster. If the entrypoint changed, a new build is live.
 *  3. On route change (useLocation): if a new build was detected AND the
 *     user hasn't snoozed it, hard-reload. This avoids blowing away a
 *     half-typed order — reloads only happen when the user navigates.
 *
 * Dev mode note: craco/CRA dev server does NOT emit asset-manifest.json.
 * The initial fetch 404s and the hook silently disables itself. Only
 * production (yarn build) has the manifest.
 */
const MANIFEST_PATH = "/asset-manifest.json";
const POLL_MS = 60_000;
const SNOOZE_KEY = "mbp:version_snoozed_for"; // sessionStorage: value = new hash the user snoozed

function extractSignature(manifest) {
  // CRA/craco shape: { files: { "main.js": "/static/js/main.<hash>.js", ... },
  //                    entrypoints: ["static/js/main.<hash>.js", ...] }
  //
  // Prefer `files["main.js"]` — in a production build it embeds the
  // content hash (`main.<hash>.js`). In dev it's a stable `bundle.js`,
  // which means the check silently no-ops in dev (correct behaviour).
  if (!manifest || typeof manifest !== "object") return null;
  const main = manifest.files?.["main.js"];
  if (main) return main;
  const entry = Array.isArray(manifest.entrypoints)
    ? manifest.entrypoints.find((f) => f.endsWith(".js") && !f.includes("hot-update"))
    : null;
  return entry || null;
}

async function fetchSignature() {
  try {
    const res = await fetch(`${MANIFEST_PATH}?t=${Date.now()}`, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!res.ok) return null;
    const json = await res.json();
    return extractSignature(json);
  } catch {
    return null;
  }
}

export default function useVersionCheck() {
  const location = useLocation();
  const initialSig = useRef(null);       // signature at page load; null = disabled
  const currentSig = useRef(null);       // latest signature seen (may differ from initial)
  const [hasNew, setHasNew] = useState(false);
  const [snoozed, setSnoozed] = useState(false);

  // Bootstrap once
  useEffect(() => {
    let cancelled = false;
    fetchSignature().then((sig) => {
      if (cancelled || !sig) return; // 404 in dev / bad manifest = disabled
      initialSig.current = sig;
      currentSig.current = sig;
    });
    return () => { cancelled = true; };
  }, []);

  // Poll + visibility/focus listeners
  useEffect(() => {
    let stopped = false;

    const check = async () => {
      if (!initialSig.current) return;
      const sig = await fetchSignature();
      if (stopped || !sig) return;
      currentSig.current = sig;
      if (sig !== initialSig.current) {
        // Respect a session-level snooze targeted at THIS specific new build
        const snoozedFor = sessionStorage.getItem(SNOOZE_KEY);
        if (snoozedFor === sig) {
          setSnoozed(true);
        }
        setHasNew(true);
      }
    };

    const interval = setInterval(check, POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      stopped = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  // Auto-reload on route change if a new build is live and user hasn't snoozed
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (hasNew && !snoozed) {
      window.location.reload();
    }
  }, [location.pathname, hasNew, snoozed]);

  const refreshNow = () => {
    // Wipe any snooze in case they snoozed then changed their mind
    sessionStorage.removeItem(SNOOZE_KEY);
    window.location.reload();
  };

  const snooze = () => {
    if (currentSig.current) {
      sessionStorage.setItem(SNOOZE_KEY, currentSig.current);
    }
    setSnoozed(true);
  };

  return { hasNew: hasNew && !snoozed, refreshNow, snooze };
}
